// Minimapa no estilo do cliente: um pixel colorido por quadrado (ampliado), centrado no personagem.
import { useEffect, useRef, useState } from 'preact/hooks';
import { minimapRgb, type ClientMap } from '../map';

const IMG = '/assets/interface/images';
const floorCache = new WeakMap<ClientMap, Map<number, HTMLCanvasElement>>();

function floorCanvas(map: ClientMap, f: number): HTMLCanvasElement {
  let byFloor = floorCache.get(map);
  if (!byFloor) floorCache.set(map, (byFloor = new Map()));
  let c = byFloor.get(f);
  if (!c) {
    c = document.createElement('canvas');
    c.width = map.w; c.height = map.h;
    const ctx = c.getContext('2d')!;
    const img = ctx.createImageData(map.w, map.h);
    for (let y = 0; y < map.h; y++) for (let x = 0; x < map.w; x++) {
      const st = map.stack(x, y, f);
      if (!st || !st.length) continue;
      const [r, g, b] = minimapRgb(map.minimapColor(x, y, f));
      const i = (y * map.w + x) * 4;
      img.data[i] = r; img.data[i + 1] = g; img.data[i + 2] = b; img.data[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    byFloor.set(f, c);
  }
  return c;
}

export interface MinimapPos { map: ClientMap | null; x: number; y: number; f: number }

function Btn(props: { name: string; title: string; onClick?: () => void }) {
  const [down, setDown] = useState(false);
  return (
    <button class="img-btn" title={props.title} aria-label={props.title} onClick={props.onClick}
      onPointerDown={() => setDown(true)} onPointerUp={() => setDown(false)} onPointerLeave={() => setDown(false)}>
      <img src={`${IMG}/automap-button-${props.name}-${down ? 'down' : 'up'}.png`} width={20} height={20} alt="" />
    </button>
  );
}

export function Minimap(props: { pos: MinimapPos | null; label?: string; onOpen?: () => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [zoom, setZoom] = useState(2);
  const [view, setView] = useState(0); // andar visto em relação ao atual
  const p = props.pos;
  const f = p ? Math.max(0, Math.min((p.map?.floors ?? 1) - 1, p.f + view)) : 0;
  useEffect(() => { setView(0); }, [p?.map, p?.f]);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, cv.width, cv.height);
    if (!p?.map) return;
    const src = floorCanvas(p.map, f);
    const tiles = cv.width / zoom;
    ctx.drawImage(src, p.x - tiles / 2 + 0.5, p.y - tiles / 2 + 0.5, tiles, tiles, 0, 0, cv.width, cv.height);
  }, [p?.map, p?.x, p?.y, f, zoom]);
  return (
    <div class="pane minimap">
      <div class="mm-map" onClick={props.onOpen} title="Clique para ver o mapa do andar inteiro" style={{ cursor: 'zoom-in' }}>
        <canvas ref={ref} width={106} height={106} />
        <i class="mm-cross" />
        <span class="mm-floor">{props.label ?? ''}{view ? ` (${view > 0 ? '-' : '+'}${Math.abs(view)})` : ''}</span>
      </div>
      <div class="mm-side">
        <img src={`${IMG}/automap-rose-idle.png`} width={43} height={43} alt="" />
        <div class="mm-btns">
          <Btn name="moveup" title="Ver andar de cima" onClick={() => setView((v) => v - 1)} />
          <Btn name="zoomin" title="Aproximar" onClick={() => setZoom((z) => Math.min(8, z * 2))} />
          <Btn name="movedown" title="Ver andar de baixo" onClick={() => setView((v) => v + 1)} />
          <Btn name="zoomout" title="Afastar" onClick={() => setZoom((z) => Math.max(1, z / 2))} />
        </div>
      </div>
    </div>
  );
}

/** Desenha o andar inteiro (mapa grande) com o personagem marcado: para o jogador se localizar na hunt/cidade */
export function MapOverlay(props: { pos: MinimapPos; onClose: () => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const p = props.pos, map = p.map;
  const [view, setView] = useState(0);
  const floors = map?.meta.hunt ? map.floors : 1;
  const f = floors > 1 ? Math.max(0, Math.min(floors - 1, p.f + view)) : p.f;
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !map) return;
    const ctx = cv.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.drawImage(floorCanvas(map, f), 0, 0, map.w, map.h, 0, 0, cv.width, cv.height);
    if (f === p.f) {
      const sx = cv.width / map.w, cx = (p.x + 0.5) * sx, cy = (p.y + 0.5) * sx;
      ctx.lineWidth = Math.max(2, sx); ctx.strokeStyle = '#fff';
      ctx.beginPath(); ctx.arc(cx, cy, Math.max(6, sx * 3), 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = '#ff3030'; ctx.fillRect(cx - Math.max(2, sx), cy - Math.max(2, sx), Math.max(4, sx * 2), Math.max(4, sx * 2));
    }
  }, [map, p.x, p.y, p.f, f]);
  if (!map) return null;
  const sc = Math.max(1, Math.floor(Math.min(4, 1400 / Math.max(map.w, map.h))));
  return (
    <div class="map-ov" onClick={props.onClose}>
      <div class="map-ov-box" onClick={(e) => e.stopPropagation()}>
        <div class="map-ov-top">
          <b>{map.meta.hunt?.name ?? 'Noxara'}{floors > 1 ? ` · andar ${f + 1} de ${floors}` : ''}</b>
          {floors > 1 && <><button disabled={f <= 0} onClick={() => setView((v) => v - 1)}>▲ andar</button><button disabled={f >= floors - 1} onClick={() => setView((v) => v + 1)}>▼ andar</button></>}
          <button onClick={props.onClose}>Fechar</button>
        </div>
        <canvas ref={ref} width={map.w * sc} height={map.h * sc} />
        <p class="map-ov-tip">{f === p.f ? 'O quadrado vermelho é você.' : 'Você está em outro andar.'}</p>
      </div>
    </div>
  );
}

/** Celular: minimapa pequeno no canto da tela do jogo; tocando abre o mapa do andar inteiro */
export function MobileMinimap(props: { pos: MinimapPos | null; onOpen: () => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [hide, setHide] = useState(() => { try { return localStorage.getItem('nx-mini-mm') === '0'; } catch { return false; } });
  const toggle = (v: boolean) => { setHide(v); try { localStorage.setItem('nx-mini-mm', v ? '0' : '1'); } catch { /* sem storage */ } };
  const p = props.pos;
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, cv.width, cv.height);
    if (!p?.map) return;
    const z = 3, tiles = cv.width / z;
    ctx.drawImage(floorCanvas(p.map, p.f), p.x - tiles / 2 + 0.5, p.y - tiles / 2 + 0.5, tiles, tiles, 0, 0, cv.width, cv.height);
  }, [p?.map, p?.x, p?.y, p?.f, hide]);
  if (!p?.map) return null;
  if (hide) return <button class="m-mm min" onClick={() => toggle(false)} title="Mostrar o minimapa">🗺</button>;
  return (
    <div class="m-mm" onClick={props.onOpen} title="Ver o mapa do andar inteiro">
      <button class="mini-ha-x" onClick={(e) => { e.stopPropagation(); toggle(true); }} aria-label="Recolher">–</button>
      <canvas ref={ref} width={96} height={96} />
      <i class="mm-cross" />
    </div>
  );
}
